import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Play, ArrowRight } from 'lucide-react'

const slides = [
  {
    id: 1,
    title: '23Controls™',
    subtitle: 'Governed AI, From Day One',
    description: 'Every unit, every agent, every AI action traces back to one accountable, verified identity. Graffiti™ OS runs under 23Controls™ governance — a compliance rulebook and audit trail built in, not bolted on.',
    cta: 'See How It Works',
    ctaLink: '/products/governance',
    accent: 'from-synth-500 to-signal-600',
    visual: 'amplifier',
  },
  {
    id: 2,
    title: 'Built for Architects',
    subtitle: 'Compliance Rules at Ingest Time',
    description: '13 live rules spanning PCI-DSS, HIPAA, PII, and CCPA/CPRA, with one canonical audit log. We score readiness per component — what’s live, what’s pilot, what’s still being built — not one blanket claim.',
    cta: 'See the Compliance Page',
    ctaLink: '/corporate/legal/compliance',
    accent: 'from-signal-500 to-synth-600',
    visual: 'network',
  },
  {
    id: 3,
    title: 'Built for Everyone Else',
    subtitle: 'Flash It. Boot It.',
    description: "You don't need to understand the governance to benefit from it. Download the image, flash it to an SD card, boot it — and you're running AI that stays accountable, by default.",
    cta: 'Our Philosophy',
    ctaLink: '/philosophy',
    accent: 'from-ember-500 to-synth-600',
    visual: 'home',
  },
  {
    id: 4,
    title: 'RAG, Multiple Models, One Pipeline',
    subtitle: 'Your Data Stays Where It Is',
    description: "Governed ingest, embedding, and local vector search — the actual RAG backbone — running where your data already lives. Swap model providers without re-architecting the pipeline.",
    cta: 'Explore RAG Workflows',
    ctaLink: '/products/enterprise/rag-workflows',
    accent: 'from-purple-500 to-synth-600',
    visual: 'language',
  },
  {
    id: 5,
    title: 'Graffiti™ Scrambler',
    subtitle: 'The Hardware, If You Want It',
    description: 'One SD card, one small unit, no data center required. The Scrambler is one way to run Graffiti™ OS — not the only way.',
    cta: 'Discover Scrambler',
    ctaLink: '/products/home',
    accent: 'from-purple-500 to-purple-700',
    visual: 'scrambler',
  },
]

function SlideVisual({ type }) {
  const baseClasses = "absolute inset-0 opacity-20"
  
  return (
    <div className={baseClasses}>
      {type === 'scrambler' && (
        <svg className="w-full h-full" viewBox="0 0 400 300">
          <defs>
            <linearGradient id="scramblerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#9333ea" />
              <stop offset="100%" stopColor="#7c3aed" />
            </linearGradient>
          </defs>
          {/* Device silhouette */}
          <motion.rect
            x="150"
            y="50"
            width="100"
            height="150"
            rx="20"
            fill="url(#scramblerGrad)"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.6, scale: 1 }}
            transition={{ duration: 1 }}
          />
          {/* Screen */}
          <motion.rect
            x="170"
            y="70"
            width="60"
            height="60"
            rx="8"
            fill="#1e1b4b"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          />
          {/* Signal waves */}
          {[1, 2, 3].map((i) => (
            <motion.circle
              key={i}
              cx="200"
              cy="100"
              r={30 + i * 25}
              stroke="#9333ea"
              strokeWidth="1"
              fill="none"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.8, 1.2, 0.8] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
            />
          ))}
        </svg>
      )}
      {type === 'language' && (
        <svg className="w-full h-full" viewBox="0 0 400 300">
          <defs>
            <linearGradient id="langGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#06b6d4" />
            </linearGradient>
          </defs>
          {[...Array(8)].map((_, i) => (
            <motion.rect
              key={i}
              x={20 + i * 45}
              y={100 + Math.sin(i) * 30}
              width={35}
              height={8}
              rx={4}
              fill="url(#langGrad)"
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 0.5 + Math.random() * 0.5, scaleX: 1 }}
              transition={{ delay: i * 0.1, duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
            />
          ))}
        </svg>
      )}
      {type === 'amplifier' && (
        <svg className="w-full h-full" viewBox="0 0 400 300">
          <motion.circle
            cx="200"
            cy="150"
            r="60"
            stroke="#06b6d4"
            strokeWidth="2"
            fill="none"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: [0.5, 1, 0.5], opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          <motion.circle
            cx="200"
            cy="150"
            r="100"
            stroke="#8b5cf6"
            strokeWidth="1"
            fill="none"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: [0.5, 1.2, 0.5], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, delay: 0.3 }}
          />
          <motion.circle
            cx="200"
            cy="150"
            r="140"
            stroke="#7c3aed"
            strokeWidth="0.5"
            fill="none"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: [0.5, 1.4, 0.5], opacity: [0.1, 0.3, 0.1] }}
            transition={{ duration: 3, repeat: Infinity, delay: 0.6 }}
          />
        </svg>
      )}
      {type === 'network' && (
        <svg className="w-full h-full" viewBox="0 0 400 300">
          {[
            { x: 100, y: 100 },
            { x: 200, y: 80 },
            { x: 300, y: 120 },
            { x: 150, y: 180 },
            { x: 250, y: 200 },
            { x: 200, y: 150 },
          ].map((node, i) => (
            <g key={i}>
              <motion.circle
                cx={node.x}
                cy={node.y}
                r={8}
                fill="#8b5cf6"
                initial={{ scale: 0 }}
                animate={{ scale: [0.8, 1.2, 0.8] }}
                transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
              />
              {i > 0 && (
                <motion.line
                  x1={node.x}
                  y1={node.y}
                  x2={200}
                  y2={150}
                  stroke="#7c3aed"
                  strokeWidth="1"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: i * 0.1 }}
                />
              )}
            </g>
          ))}
        </svg>
      )}
      {type === 'home' && (
        <svg className="w-full h-full" viewBox="0 0 400 300">
          <motion.path
            d="M200 80 L320 160 L320 260 L80 260 L80 160 Z"
            stroke="#8b5cf6"
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.rect
            x="160"
            y="200"
            width="80"
            height="60"
            stroke="#06b6d4"
            strokeWidth="2"
            fill="none"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </svg>
      )}
    </div>
  )
}

export default function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (isPaused) return
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [isPaused])

  const goToSlide = (index) => setCurrentSlide(index)
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length)
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)

  return (
    <div 
      className="relative h-screen min-h-[700px] max-h-[900px] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-void-950 via-synth-950/20 to-void-950" />
      
      {/* Grid overlay */}
      <div className="absolute inset-0 grid-overlay opacity-50" />
      
      {/* Slides */}
      <AnimatePresence mode="wait">
        {slides.map((slide, index) => (
          index === currentSlide && (
            <motion.div
              key={slide.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              {/* Slide visual background */}
              <SlideVisual type={slide.visual} />
              
              {/* Gradient accent */}
              <div className={`absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l ${slide.accent} opacity-10 blur-3xl`} />
              
              {/* Content */}
              <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                <div className="max-w-2xl">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-synth-900/50 border border-synth-700/50 text-synth-300 text-sm font-medium mb-6">
                      <span className="w-2 h-2 rounded-full bg-synth-400 animate-pulse" />
                      {slide.subtitle}
                    </span>
                  </motion.div>
                  
                  <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-5xl md:text-7xl font-display font-bold text-void-50 mb-6"
                  >
                    {slide.title}
                  </motion.h1>
                  
                  <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-lg md:text-xl text-void-300 mb-8 leading-relaxed"
                  >
                    {slide.description}
                  </motion.p>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center gap-4"
                  >
                    <Link to={slide.ctaLink} className="btn-primary flex items-center gap-2 text-base">
                      {slide.cta}
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/products/home/demo" className="btn-secondary flex items-center gap-2">
                      <Play className="w-4 h-4" />
                      Watch Demo
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )
        ))}
      </AnimatePresence>
      
      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 p-3 rounded-full bg-void-800/50 backdrop-blur-sm text-void-300 hover:text-void-50 hover:bg-void-700/50 transition-all z-20"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 p-3 rounded-full bg-void-800/50 backdrop-blur-sm text-void-300 hover:text-void-50 hover:bg-void-700/50 transition-all z-20"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
      
      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'w-8 bg-synth-500'
                : 'w-2 bg-void-600 hover:bg-void-500'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-2 text-void-500"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-void-500 to-transparent" />
      </motion.div>
    </div>
  )
}